import React, { useState, useEffect } from 'react';
import IdleTimer from 'react-idle-timer';
import { BrowserRouter, Route, Switch, Redirect } from 'react-router-dom'; // react-router v4
import { Provider } from 'react-redux';
import { ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import CssBaseline from '@mui/material/CssBaseline';

import allevaTheme from './themes/AllevaTheme';
import DA from './store/ReduxAccess';
import SocketAccess from './comm/SocketAccess';

import { markUserAsLoggedOutDueToIdleTimeout } from './store/slices/miscSlice';

// Renamed from "Admin" because CRM wants that name for its own purposes
import DevLayout from './_dev/layouts/Dev.jsx';

import UserLayout from './layouts/User.jsx';
import SuperAdminLayout from './layouts/SuperAdmin.jsx';
import AllevaRepLayout from './layouts/AllevaRep.jsx';
import PlainLayout from './layouts/Plain.jsx';
import DevModeLayout from './_dev/layouts/DevMode.jsx';

import HomeDispatcher from './components/HomeDispatcher';
import Unauthenticated from './views/Unauthenticated';
import Unauthorized from './views/Unauthorized';
import UnknownApiFailure from './views/UnknownApiFailure';

const ReduxProvider = Provider;

let idleTimer = null;
const stdTimeout = 1000 * 60 * 15; // 15 minutes
// const stdTimeout = 1000 * 20; // 20 seconds, for quicker testing
const dontLogLocalHostOutOnIdle = true;
const dontLogSuperAdminOutOnIdle = true;

const runOneTimeSetupCode = () => {
  SocketAccess.setUpWebSocket();

  window.addEventListener('beforeunload', () => {
    localStorage['lastCrmVisitTime'] = Date.now();
  });
};

const App = ({ store, onLocalMachine }) => {
  const [isIdle, setIsIdle] = useState(false);
  const [shouldLogOut, setShouldLogOut] = useState(false);

  useEffect(() => {
    runOneTimeSetupCode();
  }, []);

  const onActive = () => {
    // console.log('onActive() just fired');
    isIdle = false;
  };
  const onIdle = () => {
    // console.log('onIdle() just fired');
    isIdle = true;
    maybeLogUserOut();
  };

  function maybeLogUserOut() {
    if (onLocalMachine && dontLogLocalHostOutOnIdle) {
      console.log("Not logging user out because we're running on localhost");
      return;
    }
    const role = store.getState().userInfo.role;
    if (role === 'SuperAdmin' && dontLogSuperAdminOutOnIdle) {
      console.log("Not logging user out because s/he's a SuperAdmin");
      return;
    }
    if (DA.haveLoggedInUser()) {
      setStateToLogUserOut();
    } else {
      console.log('Not showing timeout modal because there is no logged-in user');
    }
  }

  const setStateToLogUserOut = () => {
    markUserAsLoggedOutDueToIdleTimeout(true);
    shouldLogOut = true;
  };

  const setTimerToStopRedirectingToLogOut = () => {
    // console.log("setting timer to clear shouldLogOut");
    setTimeout(() => {
      shouldLogOut = false;
    }, 1);
    return true;
  };

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={allevaTheme}>
        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
        <CssBaseline />
        <IdleTimer
          ref={(ref) => {
            idleTimer = ref;
          }}
          onActive={() => onActive()}
          onIdle={() => onIdle()}
          timeout={stdTimeout}
          startOnLoad
        />
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <ReduxProvider store={store}>
            <BrowserRouter>
              <Switch>
                {shouldLogOut && setTimerToStopRedirectingToLogOut() && <Redirect to={'logout'} />}
                <Route path="/homeDispatcher" component={HomeDispatcher} />
                <Route path="/unauthenticated" component={Unauthenticated} />
                <Route path="/unauthorized" component={Unauthorized} />
                <Route path="/unknownApiFailure" component={UnknownApiFailure} />
                <Route path="/u/" component={UserLayout} />
                <Route path="/sa/" component={SuperAdminLayout} />
                <Route path="/ar/" component={AllevaRepLayout} />
                <Route path="/p/" component={PlainLayout} />
                <Route path="/dev/" component={DevLayout} />
                <Route path="/dm/" component={DevModeLayout} />
                {onLocalMachine ? <Redirect from="/" to="/dev/home" /> : <Redirect from="/" to="/homeDispatcher" />}
              </Switch>
            </BrowserRouter>
          </ReduxProvider>
        </LocalizationProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export default App;
